import {translateFunction} from "../../../../localization/TranslationService";
import {Alert, Box, Button, CircularProgress, Paper, TableBody, TableFooter, TableHead, TextField, Typography} from "@mui/material";
import React, {useState} from "react";
import {useQuery} from "@apollo/client";
import {PRODUCT_CUSTOM_ATTRIBUTE_BULK_UPDATE, PRODUCTS_FOR_SUPPLIER} from "../../Queries";
import {graphQLServices} from "../../../../services/GraphQLServices";
import {productsEditorDefaultTargetStage, productsEditorTargetStage} from "../../ProductModuleOverview";
import {CustomAttributeSetup} from "../../models/CustomAttributeSetup";
import {Product} from "../../models/Product";
import TableContainer from "@mui/material/TableContainer";
import Table from "@mui/material/Table";
import TableRow from "@mui/material/TableRow";
import TableCell from "@mui/material/TableCell";
import {BulkRowProps} from "../models/BulkRowProps";
import {CustomAttributeBulkUpdateBatchInput} from "../../models/CustomAttributeBulkUpdateBatchInput";
import {CustomAttributeBulkUpdateInput} from "../../models/CustomAttributeBulkUpdateInput";
import {GlobalState} from "../../../../global/GlobalState";

const translate = translateFunction("SureBasePortalProductsCatalog");

export interface CustomAttributeBulkEditorPerSupplierProps {
	supplierKrn: string;
	customAttributes: CustomAttributeSetup[];
	
}

export function CustomAttributeBulkEditorProducts(props: CustomAttributeBulkEditorPerSupplierProps) {
	const stage = GlobalState.targetKiziStage(productsEditorDefaultTargetStage);
	const {loading: loadingProducts, data: productsData, error: productsError} = useQuery(PRODUCTS_FOR_SUPPLIER, {
		client: graphQLServices.productsClient,
		fetchPolicy: "no-cache",
		variables: {
			stage: productsEditorTargetStage,
			productSupplierKrn: props.supplierKrn
		}
	});
	
	const [products, setProducts] = useState<Product[]>();
	const [valueLookup, setValueLookup] = useState<Map<string, string>>();
	const [rows, setRows] = useState<BulkRowProps[]>();
	const [isSaveSuccessful, setIsSaveSuccessful] = useState(false);
	
	if (loadingProducts)
		return <Box sx={{display: "flex", width: "100%", height: "100%"}}>
			<CircularProgress style={{margin: "auto"}}/>
		</Box>;
	
	if (productsError)
		throw productsError;
	
	const productNodes = productsData.products.nodes;
	if (productNodes === null || productNodes.length === 0)
		throw new Error(translate(`Products not found.`));
	
	if (!products) {
		productNodes.sort((a:Product, b:Product) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
		setProducts(productNodes);
	}
	if (props.customAttributes && products && !valueLookup) {
		const values = createValueLookup(products);
		const bulkRows = createBulkRows(props.customAttributes, products, values);
		setValueLookup(values);
		setRows(bulkRows);
	}
	
	function createValueLookup(products: Product[]): Map<string, string> {
		const result = new Map<string, string>();
		for (const product of products) {
			if (product.customAttributes) {
				for (const customAttribute of product.customAttributes) {
					result.set(product.krn + customAttribute.description, customAttribute.value);
				}
				
			}
		}
		return result;
	}
	
	function createBulkRows(attributes: CustomAttributeSetup[], products: Product[], valueLookup: Map<string, string>): BulkRowProps[] {
		const result: BulkRowProps[] = [];
		for (const product of products) {
			const row: BulkRowProps = {
				cells: [],
				id: product.krn,
				krn: product.krn,
				description: product.name,
				dirty:false
			};
			for (const attribute of attributes) {
				const key = product.krn + attribute.description;
				if (valueLookup.has(key))
					row.cells.push({
						id: attribute.id,
						column: attribute.description,
						value: valueLookup.get(key) ?? ""
					});
				else
					row.cells.push({
						id: attribute.id,
						column: attribute.description,
						value: undefined
					});
			}
			result.push(row);
		}
		
		return result;
	}
	
	function createCustomAttributeBulkUpdateInput(records: BulkRowProps[]): CustomAttributeBulkUpdateBatchInput {
		const result: CustomAttributeBulkUpdateBatchInput = {
			items: []
		};
		
		for (const record of records) {
			if (!record.dirty)
				continue;
			
			const product: CustomAttributeBulkUpdateInput = {
				krn: record.krn,
				customAttributes: []
			};
			if (record.cells) {
				for (const cell of record.cells) {
					if (!cell.value || cell.value === "")
						continue;
					product.customAttributes.push({
						value: cell.value,
						description: cell.column
					});
				}
			}
			result.items.push(product);
		}
		
		return result;
	}
	
	async function saveChanges() {
		if (!rows || rows.length === 0)
			return;
		const batch = createCustomAttributeBulkUpdateInput(rows);
		if (batch.items.length === 0)
			return;
		await graphQLServices.productsClient.mutate({
			mutation: PRODUCT_CUSTOM_ATTRIBUTE_BULK_UPDATE,
			variables: {
				stage: productsEditorTargetStage,
				batch: batch,
			}
		});
		setIsSaveSuccessful(true);
	}
	
	const handleCloseAlert = () => {
		setIsSaveSuccessful(false);
	};
	
	return (
		<>
			{isSaveSuccessful && <Alert
				severity="success"
				onClose={handleCloseAlert}
			>
				{translate("Successfully saved")}
			</Alert>}
			<TableContainer component={Paper}>
				<Table>
					<TableHead>
						<TableRow>
							<TableCell>
								<Typography>Krn</Typography>
							</TableCell>
							<TableCell>
								<Typography>{translate("Product")}</Typography>
							</TableCell>
							{props.customAttributes?.map((header, index) => (
								<TableCell key={index}>
									<Typography>{header.description}</Typography>
								</TableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{rows?.map((row, rowIndex) => (
							<TableRow key={rowIndex}>
								<TableCell>
									<Typography>{row.krn}</Typography>
								</TableCell>
								<TableCell>
									<Typography>{row.description}</Typography>
								</TableCell>
								{row.cells.map((cell, cellIndex) => (
									<TableCell key={cellIndex}>
										<TextField
											style={{width: "500px"}}
											id={cell.id}
											size={"small"}
											value={cell.value}
											multiline
											placeholder={cell.column}
											onChange={event => {
												const clone = [...rows];
												clone[rowIndex].cells[cellIndex].value = event.target.value;
												clone[rowIndex].dirty = true;
												setRows(clone);
											}}
										/>
									</TableCell>
								))}
							</TableRow>
						))}
					</TableBody>
					<TableFooter>
						<TableRow>
							<TableCell>
								{stage=== productsEditorTargetStage &&
									<Button onClick={saveChanges} color={"success"}>{translate("Save changes")}</Button>
								}
								{stage!== productsEditorTargetStage &&
									<Typography>{translate("Editing only allowed on Dev")}</Typography>
								}
							</TableCell>
						</TableRow>
					</TableFooter>
				</Table>
			</TableContainer>
		</>
	);
}
